import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import {
  FileText, DollarSign, Package, Users, Calendar, Clock,
  ArrowLeft, Save, AlertTriangle, CheckCircle, Info, History,
  ShoppingCart, TrendingUp, Building, Banknote, Briefcase, UserCog
} from 'lucide-react';

const TRANSACTION_TYPES = [
  { value: 'sale', label: 'Sale', icon: ShoppingCart, color: 'green' },
  { value: 'expense', label: 'Expense', icon: DollarSign, color: 'red' },
  { value: 'stock_received', label: 'Stock Received', icon: Package, color: 'blue' },
  { value: 'stock_issued', label: 'Stock Issued', icon: Package, color: 'orange' },
  { value: 'inventory_adjustment', label: 'Inventory Adjustment', icon: TrendingUp, color: 'purple' },
  { value: 'customer_payment', label: 'Customer Payment', icon: Users, color: 'teal' },
  { value: 'supplier_payment', label: 'Supplier Payment', icon: Building, color: 'indigo' },
  { value: 'payroll', label: 'Payroll', icon: Briefcase, color: 'pink' },
  { value: 'appointment', label: 'Appointment', icon: Calendar, color: 'yellow' },
  { value: 'patient_record', label: 'Patient Record', icon: FileText, color: 'cyan' },
  { value: 'asset_purchase', label: 'Asset Purchase', icon: Building, color: 'slate' },
  { value: 'bank_transaction', label: 'Bank Transaction', icon: Banknote, color: 'emerald' },
  { value: 'leave_records', label: 'Leave Records', icon: UserCog, color: 'violet' },
  { value: 'other', label: 'Other', icon: Info, color: 'gray' }
];

const EXPENSE_CATEGORIES = [
  'rent', 'salaries', 'utilities', 'internet', 'marketing',
  'inventory_purchase', 'equipment', 'miscellaneous'
];

const LEAVE_TYPES = [
  'annual', 'sick', 'personal', 'maternity', 'paternity', 'other'
];

const ASSET_CATEGORIES = [
  'equipment', 'furniture', 'vehicle', 'computer', 'building', 'other'
];

const BANK_TRANSACTION_TYPES = [
  'deposit', 'withdrawal', 'transfer', 'fee', 'interest'
];

const HistoricalDataEntry = () => {
  const [step, setStep] = useState(1); // 1: select type, 2: fill form, 3: review
  const [selectedType, setSelectedType] = useState(null);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [periodCheck, setPeriodCheck] = useState(null);
  const [migrationMode, setMigrationMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const [formData, setFormData] = useState({
    transaction_date: '',
    description: '',
    reference_number: '',
    amount: '',
    quantity: '',
    unit_price: '',
    reason_for_backdating: '',
    product_id: '',
    client_id: '',
    supplier_id: '',
    staff_id: '',
    category: 'miscellaneous',
    payment_method: 'cash',
    notes: '',
    adjustment_type: 'add',
    // For sales
    items: [{ product_id: '', quantity: 1, unit_price: '', item_type: 'product' }],
    // For appointments
    appointment_time: '09:00',
    appointment_type: 'initial_consultation',
    appointment_status: 'completed',
    // For leave records
    leave_type: 'annual',
    leave_start_date: '',
    leave_end_date: '',
    leave_status: 'approved',
    // For assets
    asset_name: '',
    asset_category: 'equipment',
    asset_purchase_cost: '',
    asset_location: '',
    asset_serial_number: '',
    // For bank transactions
    bank_name: 'General',
    bank_account_number: '',
    bank_tx_type: 'deposit',
    // For payroll
    pay_period_start: '',
    pay_period_end: '',
    basic_pay: '',
    allowances: '',
    deductions: '',
    payment_method_payroll: 'bank_transfer'
  });

  useEffect(() => {
    loadInitialData();
    checkMigrationMode();
  }, []);

  const loadInitialData = async () => {
    try {
      const [productsRes, clientsRes, staffRes, suppliersRes] = await Promise.all([
        api.get('/inventory'),
        api.get('/clients'),
        api.get('/staff'),
        api.get('/suppliers')
      ]);
      setProducts(productsRes.data);
      setClients(clientsRes.data);
      setStaff(staffRes.data);
      setSuppliers(suppliersRes.data);
    } catch (err) {
      console.error('Error loading data:', err);
    }
  };

  const checkMigrationMode = async () => {
    try {
      const res = await api.get('/historical/migration-mode');
      setMigrationMode(res.data.migration_mode);
    } catch (err) {
      console.error('Error checking migration mode:', err);
    }
  };

  const checkPeriod = async (date) => {
    if (!date) return;
    try {
      const res = await api.get(`/historical/periods/check?date=${date}`);
      setPeriodCheck(res.data);
      if (!res.data.allowed && !migrationMode) {
        toast.warning(res.data.message);
      }
    } catch (err) {
      console.error('Period check error:', err);
    }
  };

  const handleDateChange = (e) => {
    const date = e.target.value;
    setFormData(prev => ({ ...prev, transaction_date: date }));
    if (date) checkPeriod(date);
  };

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setStep(2);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setFormData(prev => ({ ...prev, items: updatedItems }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { product_id: '', quantity: 1, unit_price: '', item_type: 'product' }]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const canSubmit = () => {
    if (!formData.transaction_date) return false;
    if (!formData.reason_for_backdating && !migrationMode) return false;
    
    switch (selectedType) {
      case 'expense':
        return formData.amount && formData.description;
      case 'stock_received':
      case 'stock_issued':
      case 'inventory_adjustment':
        return formData.product_id && formData.quantity;
      case 'sale':
        return formData.items.some(i => i.product_id && i.quantity);
      case 'appointment':
      case 'patient_record':
        return formData.client_id;
      case 'customer_payment':
        return formData.client_id && formData.amount;
      case 'supplier_payment':
        return formData.supplier_id && formData.amount;
      case 'payroll':
        return formData.staff_id && formData.basic_pay;
      case 'leave_records':
        return formData.staff_id && formData.leave_start_date;
      case 'asset_purchase':
        return formData.asset_name && formData.asset_purchase_cost;
      case 'bank_transaction':
        return formData.bank_name && formData.amount;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        transaction_type: selectedType,
        transaction_date: formData.transaction_date,
        description: formData.description || `${selectedType.replace(/_/g, ' ')} entry`,
        reference_number: formData.reference_number,
        amount: parseFloat(formData.amount) || null,
        quantity: parseInt(formData.quantity) || null,
        unit_price: parseFloat(formData.unit_price) || null,
        reason_for_backdating: formData.reason_for_backdating,
        product_id: formData.product_id || null,
        client_id: formData.client_id || null,
        supplier_id: formData.supplier_id || null,
        staff_id: formData.staff_id || null,
        category: formData.category,
        payment_method: formData.payment_method,
        notes: formData.notes,
        adjustment_type: formData.adjustment_type,
        items: selectedType === 'sale' ? formData.items.filter(i => i.product_id).map(i => ({
          product_id: i.product_id,
          quantity: parseInt(i.quantity) || 1,
          unit_price: parseFloat(i.unit_price) || 0,
          item_type: 'product'
        })) : undefined,
        appointment_time: formData.appointment_time,
        appointment_type: formData.appointment_type,
        appointment_status: formData.appointment_status,
        // Leave records
        leave_type: formData.leave_type,
        leave_start_date: formData.leave_start_date,
        leave_end_date: formData.leave_end_date,
        leave_status: formData.leave_status,
        // Assets
        asset_name: formData.asset_name,
        asset_category: formData.asset_category,
        asset_purchase_cost: parseFloat(formData.asset_purchase_cost) || null,
        asset_location: formData.asset_location,
        asset_serial_number: formData.asset_serial_number,
        // Bank transactions
        bank_name: formData.bank_name,
        bank_account_number: formData.bank_account_number,
        bank_tx_type: formData.bank_tx_type,
        // Payroll
        pay_period_start: formData.pay_period_start,
        pay_period_end: formData.pay_period_end,
        basic_pay: parseFloat(formData.basic_pay) || null,
        allowances: parseFloat(formData.allowances) || 0,
        deductions: parseFloat(formData.deductions) || 0,
        payment_method_payroll: formData.payment_method_payroll
      };

      const res = await api.post('/historical/entries', payload);
      setResult(res.data);
      setStep(3);
      toast.success('Historical entry created successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create historical entry');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSelectedType(null);
    setFormData({
      transaction_date: '',
      description: '',
      reference_number: '',
      amount: '',
      quantity: '',
      unit_price: '',
      reason_for_backdating: '',
      product_id: '',
      client_id: '',
      supplier_id: '',
      staff_id: '',
      category: 'miscellaneous',
      payment_method: 'cash',
      notes: '',
      adjustment_type: 'add',
      items: [{ product_id: '', quantity: 1, unit_price: '', item_type: 'product' }],
      appointment_time: '09:00',
      appointment_type: 'initial_consultation',
      appointment_status: 'completed',
      leave_type: 'annual',
      leave_start_date: '',
      leave_end_date: '',
      leave_status: 'approved',
      asset_name: '',
      asset_category: 'equipment',
      asset_purchase_cost: '',
      asset_location: '',
      asset_serial_number: '',
      bank_name: 'General',
      bank_account_number: '',
      bank_tx_type: 'deposit',
      pay_period_start: '',
      pay_period_end: '',
      basic_pay: '',
      allowances: '',
      deductions: '',
      payment_method_payroll: 'bank_transfer'
    });
    setPeriodCheck(null);
    setResult(null);
  };

  const TypeIcon = selectedType ? TRANSACTION_TYPES.find(t => t.value === selectedType)?.icon : Info;

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <History className="w-6 h-6 text-amber-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Historical Data Entry</h1>
            <p className="text-sm text-gray-500">Enter backdated transactions with full audit trail</p>
          </div>
        </div>
        {migrationMode && (
          <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium flex items-center">
            <AlertTriangle size={14} className="mr-1" />
            Migration Mode Active
          </span>
        )}
      </div>

      {/* Step 1: Select Transaction Type */}
      {step === 1 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Select Transaction Type</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {TRANSACTION_TYPES.map((type) => {
              const Icon = type.icon;
              const isSelected = selectedType === type.value;
              return (
                <button
                  key={type.value}
                  onClick={() => handleTypeSelect(type.value)}
                  className={`p-4 rounded-xl border-2 transition-all text-left
                    ${isSelected 
                      ? 'border-amber-500 bg-amber-50 shadow-md' 
                      : 'border-gray-200 bg-white hover:border-amber-300 hover:shadow'
                    }`}
                >
                  <Icon className={`w-8 h-8 mb-2 ${isSelected ? 'text-amber-600' : 'text-gray-400'}`} />
                  <div className="text-sm font-medium text-gray-800">{type.label}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 2: Fill Form */}
      {step === 2 && selectedType && (
        <div className="space-y-6">
          <button
            onClick={() => setStep(1)}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={16} className="mr-1" />
            Change transaction type
          </button>

          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center space-x-2 mb-6">
              {TypeIcon && <TypeIcon className="w-5 h-5 text-amber-600" />}
              <h2 className="text-lg font-semibold">
                {TRANSACTION_TYPES.find(t => t.value === selectedType)?.label}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Transaction Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transaction Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.transaction_date}
                  onChange={handleDateChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
                {periodCheck && (
                  <div className={`mt-1 flex items-center text-xs ${
                    periodCheck.allowed ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {periodCheck.allowed 
                      ? <CheckCircle size={12} className="mr-1" /> 
                      : <AlertTriangle size={12} className="mr-1" />
                    }
                    {periodCheck.message}
                  </div>
                )}
              </div>

              {/* Entry Date (auto) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Entry Date (today)
                </label>
                <input
                  type="date"
                  value={new Date().toISOString().split('T')[0]}
                  disabled
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                />
              </div>

              {/* Reason for Backdating */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Backdating {!migrationMode && <span className="text-red-500">*</span>}
                  {migrationMode && <span className="text-gray-400 text-xs ml-1">(optional in migration mode)</span>}
                </label>
                <textarea
                  value={formData.reason_for_backdating}
                  onChange={(e) => handleInputChange('reason_for_backdating', e.target.value)}
                  placeholder="e.g., Migrating old records, correcting previous entry..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
              </div>

              {/* Type-specific fields */}
              {(selectedType === 'expense') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    >
                      {EXPENSE_CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{cat.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => handleInputChange('amount', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                    <select
                      value={formData.payment_method}
                      onChange={(e) => handleInputChange('payment_method', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="cash">Cash</option>
                      <option value="mpesa">M-Pesa</option>
                      <option value="card">Card</option>
                      <option value="bank_transfer">Bank Transfer</option>
                    </select>
                  </div>
                </>
              )}

              {(selectedType === 'stock_received' || selectedType === 'stock_issued' || selectedType === 'inventory_adjustment') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Product *</label>
                    <select
                      value={formData.product_id}
                      onChange={(e) => handleInputChange('product_id', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="">Select product</option>
                      {products.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name} (Stock: {p.quantity_in_stock})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                    <input
                      type="number"
                      value={formData.quantity}
                      onChange={(e) => handleInputChange('quantity', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  {selectedType === 'inventory_adjustment' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Adjustment Type</label>
                      <select
                        value={formData.adjustment_type}
                        onChange={(e) => handleInputChange('adjustment_type', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="add">Add Stock</option>
                        <option value="remove">Remove Stock</option>
                        <option value="damaged">Damaged</option>
                        <option value="expired">Expired</option>
                      </select>
                    </div>
                  )}
                  {(selectedType === 'stock_received' || selectedType === 'stock_issued') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Unit Price</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.unit_price}
                        onChange={(e) => handleInputChange('unit_price', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                      />
                    </div>
                  )}
                </>
              )}

              {selectedType === 'sale' && (
                <div className="md:col-span-2 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                    <select
                      value={formData.client_id}
                      onChange={(e) => handleInputChange('client_id', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="">Walk-in (no client)</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                    <select
                      value={formData.payment_method}
                      onChange={(e) => handleInputChange('payment_method', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="cash">Cash</option>
                      <option value="mpesa">M-Pesa</option>
                      <option value="card">Card</option>
                      <option value="bank_transfer">Bank Transfer</option>
                    </select>
                  </div>

                  {/* Sale Items */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">Sale Items</label>
                      <button
                        onClick={addItem}
                        className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                      >
                        + Add Item
                      </button>
                    </div>
                    {formData.items.map((item, idx) => (
                      <div key={idx} className="flex items-center space-x-2 mb-2">
                        <select
                          value={item.product_id}
                          onChange={(e) => handleItemChange(idx, 'product_id', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                        >
                          <option value="">Select product</option>
                          {products.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                        <input
                          type="number"
                          placeholder="Qty"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                          className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                        />
                        <input
                          type="number"
                          step="0.01"
                          placeholder="Price"
                          value={item.unit_price}
                          onChange={(e) => handleItemChange(idx, 'unit_price', e.target.value)}
                          className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                        />
                        {formData.items.length > 1 && (
                          <button
                            onClick={() => removeItem(idx)}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(selectedType === 'appointment' || selectedType === 'patient_record') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
                    <select
                      value={formData.client_id}
                      onChange={(e) => handleInputChange('client_id', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="">Select client</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Appointment Time</label>
                    <input
                      type="time"
                      value={formData.appointment_time}
                      onChange={(e) => handleInputChange('appointment_time', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      value={formData.appointment_type}
                      onChange={(e) => handleInputChange('appointment_type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="initial_consultation">Initial Consultation</option>
                      <option value="follow_up">Follow Up</option>
                      <option value="program_review">Program Review</option>
                      <option value="assessment">Assessment</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={formData.appointment_status}
                      onChange={(e) => handleInputChange('appointment_status', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="completed">Completed</option>
                      <option value="scheduled">Scheduled</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </>
              )}

              {(selectedType === 'customer_payment' || selectedType === 'supplier_payment') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {selectedType === 'customer_payment' ? 'Client' : 'Supplier'} *
                    </label>
                    {selectedType === 'customer_payment' ? (
                      <select
                        value={formData.client_id}
                        onChange={(e) => handleInputChange('client_id', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="">Select client</option>
                        {clients.map(c => (
                          <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                        ))}
                      </select>
                    ) : (
                      <select
                        value={formData.supplier_id}
                        onChange={(e) => handleInputChange('supplier_id', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="">Select supplier</option>
                        {suppliers.map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => handleInputChange('amount', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                    <select
                      value={formData.payment_method}
                      onChange={(e) => handleInputChange('payment_method', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="cash">Cash</option>
                      <option value="mpesa">M-Pesa</option>
                      <option value="bank_transfer">Bank Transfer</option>
                    </select>
                  </div>
                </>
              )}

              {(selectedType === 'payroll') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Staff *</label>
                    <select
                      value={formData.staff_id}
                      onChange={(e) => handleInputChange('staff_id', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="">Select staff</option>
                      {staff.map(s => (
                        <option key={s.id} value={s.id}>{s.full_name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Basic Pay *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.basic_pay}
                      onChange={(e) => handleInputChange('basic_pay', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Allowances</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.allowances}
                      onChange={(e) => handleInputChange('allowances', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Deductions</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.deductions}
                      onChange={(e) => handleInputChange('deductions', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                    <select
                      value={formData.payment_method_payroll}
                      onChange={(e) => handleInputChange('payment_method_payroll', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="cash">Cash</option>
                      <option value="mpesa">M-Pesa</option>
                    </select>
                  </div>
                </>
              )}

              {(selectedType === 'leave_records') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Staff *</label>
                    <select
                      value={formData.staff_id}
                      onChange={(e) => handleInputChange('staff_id', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="">Select staff</option>
                      {staff.map(s => (
                        <option key={s.id} value={s.id}>{s.full_name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type</label>
                    <select
                      value={formData.leave_type}
                      onChange={(e) => handleInputChange('leave_type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    >
                      {LEAVE_TYPES.map(type => (
                        <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                    <input
                      type="date"
                      value={formData.leave_start_date}
                      onChange={(e) => handleInputChange('leave_start_date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                    <input
                      type="date"
                      value={formData.leave_end_date}
                      onChange={(e) => handleInputChange('leave_end_date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={formData.leave_status}
                      onChange={(e) => handleInputChange('leave_status', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="approved">Approved</option>
                      <option value="pending">Pending</option>
                      <option value="rejected">Rejected</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </>
              )}

              {(selectedType === 'asset_purchase') && (
                <>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Asset Name *</label>
                    <input
                      type="text"
                      value={formData.asset_name}
                      onChange={(e) => handleInputChange('asset_name', e.target.value)}
                      placeholder="e.g., Dell Laptop, Office Chair"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      value={formData.asset_category}
                      onChange={(e) => handleInputChange('asset_category', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    >
                      {ASSET_CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Cost *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.asset_purchase_cost}
                      onChange={(e) => handleInputChange('asset_purchase_cost', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                    <input
                      type="text"
                      value={formData.asset_location}
                      onChange={(e) => handleInputChange('asset_location', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
                    <input
                      type="text"
                      value={formData.asset_serial_number}
                      onChange={(e) => handleInputChange('asset_serial_number', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                </>
              )}

              {(selectedType === 'bank_transaction') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name *</label>
                    <input
                      type="text"
                      value={formData.bank_name}
                      onChange={(e) => handleInputChange('bank_name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                    <input
                      type="text"
                      value={formData.bank_account_number}
                      onChange={(e) => handleInputChange('bank_account_number', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Type</label>
                    <select
                      value={formData.bank_tx_type}
                      onChange={(e) => handleInputChange('bank_tx_type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    >
                      {BANK_TRANSACTION_TYPES.map(type => (
                        <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => handleInputChange('amount', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                </>
              )}

              {(selectedType === 'payroll' || selectedType === 'asset_purchase' || selectedType === 'bank_transaction' || selectedType === 'other' || selectedType === 'leave_records') && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder={`Describe this ${selectedType.replace(/_/g, ' ')}...`}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              )}

              {/* Notes for all types */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-8 flex justify-end space-x-3">
              <button
                onClick={resetForm}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!canSubmit() || submitting}
                className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {submitting ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Processing...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Save size={16} className="mr-2" />
                    Create Historical Entry
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Result */}
      {step === 3 && result && (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="text-center mb-6">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-gray-900">Historical Entry Created</h2>
            <p className="text-gray-500 mt-1">The transaction has been recorded and system has been recalculated.</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-gray-800 mb-3">Transaction Details</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500">Type:</span>
                <span className="ml-2 font-medium">{result.historical_transaction?.transaction_type}</span>
              </div>
              <div>
                <span className="text-gray-500">Date:</span>
                <span className="ml-2 font-medium">{result.historical_transaction?.transaction_date}</span>
              </div>
              <div>
                <span className="text-gray-500">Entry Date:</span>
                <span className="ml-2 font-medium">{new Date(result.historical_transaction?.entry_date).toLocaleDateString()}</span>
              </div>
              <div>
                <span className="text-gray-500">Entered By:</span>
                <span className="ml-2 font-medium">{result.historical_transaction?.entered_by_name}</span>
              </div>
              {result.historical_transaction?.reason_for_backdating && (
                <div className="col-span-2">
                  <span className="text-gray-500">Reason:</span>
                  <span className="ml-2 font-medium">{result.historical_transaction?.reason_for_backdating}</span>
                </div>
              )}
            </div>
          </div>

          {result.recalculation && (
            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <h3 className="font-medium text-blue-800 mb-2">Recalculation Results</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="bg-white rounded p-2 text-center">
                  <div className="text-lg font-bold text-blue-600">{result.recalculation.inventory?.products_processed || 0}</div>
                  <div className="text-xs text-gray-500">Products Updated</div>
                </div>
                <div className="bg-white rounded p-2 text-center">
                  <div className="text-lg font-bold text-blue-600">{result.recalculation.customer_balances?.length || 0}</div>
                  <div className="text-xs text-gray-500">Customer Balances</div>
                </div>
                <div className="bg-white rounded p-2 text-center">
                  <div className="text-lg font-bold text-blue-600">
                    {result.recalculation.financial?.success ? '✓' : '✗'}
                  </div>
                  <div className="text-xs text-gray-500">Financial Reports</div>
                </div>
                <div className="bg-white rounded p-2 text-center">
                  <div className="text-lg font-bold text-blue-600">
                    {new Date(result.recalculation.recalculated_at).toLocaleTimeString()}
                  </div>
                  <div className="text-xs text-gray-500">Recalculated At</div>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-center">
            <button
              onClick={resetForm}
              className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
            >
              Enter Another Transaction
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoricalDataEntry;